import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import {
  Feature,
  FeatureNames,
  HolidayProgramCommands,
  HolidayProgramProperties,
} from '@vi-iot/client';

import {
  DevicesStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  Platform,
} from '@store';

import { HolidayDatepickerModalComponent } from '../holiday-mode-panel/holiday-datepicker-modal/holiday-datepicker-modal.component';
import { HolidayProgramType } from '../holiday-mode-panel/holiday-mode-select/holiday-mode-select.component';

@Component({
  selector: 'app-set-holiday-mode',
  templateUrl: './set-holiday-mode.component.html',
  styleUrls: ['./set-holiday-mode.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetHolidayModeComponent implements OnInit, OnDestroy {
  @Input() circuitId: string;
  @Input() displayText: string;
  @Input() holidayType: HolidayProgramType;
  @Input() isRemoteDiagnostic: boolean;

  holidaySingleFamily$: Observable<Feature<HolidayProgramProperties, HolidayProgramCommands>>;
  holidayMultiFamily$: Observable<Feature<HolidayProgramProperties, HolidayProgramCommands>>;

  isMultiFamily: boolean;

  platform: Platform;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public translation: TranslateService,
    private devicesStore: DevicesStore,
    private installationDetailsStore: InstallationDetailsStore,
    private datepickerDialog: MatDialog,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.installationDetailsStore
      .getMainDevicePlatform()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((platform) => !!platform),
        map((platform) => platform)
      )
      .subscribe((platform) => {
        this.platform = platform;
      });

    this.holidaySingleFamily$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitSingleFamilyHolidayProgram
    );

    this.holidayMultiFamily$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitHolidayProgram(this.circuitId)
    );

    this.devicesStore.isMultifamily$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((isMulti) => {
      this.isMultiFamily = isMulti;
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  openDatepickerDialog(): void {
    const dialogRef = this.datepickerDialog.open(HolidayDatepickerModalComponent, {
      width: '583px',
      data: {
        platform: this.platform,
        circuitId: this.circuitId,
        holidayType: this.holidayType,
        isMultiFamily: this.isMultiFamily,
      },
      panelClass: 'datepicker-dialog',
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((data) => !isEmpty(data))
      )
      .subscribe(({ selectedHolidayType, startDate, endDate, circuitId }) => {
        this.onHolidayModeSchedule(selectedHolidayType, startDate, endDate, circuitId);
      });
  }

  onHolidayModeSchedule(
    holidayProgramType: HolidayProgramType,
    startDate: Date,
    endDate: Date,
    circuitId: string
  ): void {
    if (this.circuitId !== circuitId && this.holidayType) {
      this.installationFeaturesStore.updateFeature(
        this.getFeatureName(holidayProgramType, this.circuitId),
        [false, '', ''],
        ['active', 'start', 'end'],
        null,
        'unschedule'
      );
    }

    this.installationFeaturesStore.updateFeature(
      this.getFeatureName(holidayProgramType, circuitId),
      [this.toLocaleDate(startDate), this.toLocaleDate(endDate)],
      ['start', 'end'],
      ['start', 'end'],
      'schedule'
    );
  }

  /**
   * Returns a locale date in YYYY-MM-DD format
   *
   * @param date
   */

  toLocaleDate(date: Date): string {
    if (!date) {
      return undefined;
    }
    const s = date
      .toLocaleDateString('en-GB')
      .split('/')
      .map((i) => +i)
      .map((i) => (i < 10 ? `0${i}` : `${i}`));

    return `${s[2]}-${s[1]}-${s[0]}`;
  }

  private getFeatureName(holidayProgramType: HolidayProgramType, circuitId: string): string {
    let featureName: string;
    if (this.isMultiFamily) {
      featureName =
        holidayProgramType === HolidayProgramType.HOLIDAY_AT_HOME
          ? FeatureNames.circuitHolidayAtHome(circuitId)
          : FeatureNames.circuitHolidayProgram(circuitId);
    } else {
      featureName =
        holidayProgramType === HolidayProgramType.HOLIDAY_AT_HOME
          ? FeatureNames.circuitSingleFamilyHolidayAtHome
          : FeatureNames.circuitSingleFamilyHolidayProgram;
    }

    return featureName;
  }
}
